import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, Stack, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { AddCovenantDrawer } from "components/Deal-covanents/AddCovenantDrawer";
import { Menu } from "components/popups/Menu/Menu";
import { InfoCard } from "components/reports";
import { MemoizedHighchart } from "components/reports/Chart/MemoizedHighchart";
import { useActionHook, useQueryHook } from "hooks";
import {
  useDealActions,
  useDealIdSelector,
  useDealSubAssetsSelector,
  useDealTitleSelector,
  useReportsActions,
  useReportsQueriesSelector
} from "store";
import { Covenant } from "store/types/deal/covanents";

export const DebtReport: React.FC = () => {
  const { getCubicAction } = useReportsActions();
  const dealId = useDealIdSelector();
  const dealTitle = useDealTitleSelector();
  const subAssets = useDealSubAssetsSelector();
  const queries = useReportsQueriesSelector();
  const { getDealCovenantsAction, deleteCovenantAction } = useDealActions();

  const { dateRange, currency, exchangeMethod, use_accounting_date, cubicDeal } = queries;

  const [showChildren] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCovenant, setEditingCovenant] = useState<Covenant | null>(null);

  const {
    data: cubicData,
    action: getCubic,
    loading: cubicLoading //pageType: cubicSnapshot, cubicDeal
  } = useActionHook(getCubicAction);

  const { action: deleteCovenant } = useActionHook(deleteCovenantAction, {
    onSuccess: () => {
      refetch();
    }
  });

  const {
    data: covenants,
    loading,
    refetch
  } = useQueryHook(() => (dealId ? getDealCovenantsAction(dealId) : Promise.resolve([])));

  useEffect(() => {
    if (Object.keys(cubicDeal).length && dealTitle) {
      const subAssetNames = subAssets.map((asset) => asset.title);
      getCubic({
        cubics: cubicDeal,
        pageType: "cubicDeal",
        filters: { ["deal_name"]: showChildren ? [dealTitle, ...subAssetNames] : [dealTitle] },
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
  }, [dealId, cubicDeal]);

  const charts = useMemo(() => {
    return (covenants || []).map((covenant: Covenant) => {
      return {
        ...covenant,
        graph: {
          ...covenant.graph,
          chart: {
            height: 85,
            backgroundColor: "transparent"
          },
          yAxis: {
            ...covenant.graph.yAxis,
            title: { text: "" }
          },
          title: {
            text: undefined
          },
          exporting: { enabled: false },
          tooltip: {
            shared: true
          },
          legend: {
            enabled: false
          },
          series: covenant.graph.series,
          responsive: { enabled: false },
          credits: { enabled: false }
        }
      };
    });
  }, [covenants]);

  return (
    <Stack
      sx={{
        gap: "22px",
        flexGrow: 1,
        borderRadius: "8px",
        padding: "22px",
        overflow: "auto"
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "14px"
        }}
      >
        {cubicData &&
          Object.entries(cubicData).map(([name, data], index) => (
            <InfoCard pageType="cubicDeal" isLoading={cubicLoading} name={name} {...data} key={index} />
          ))}
      </Box>

      <Box>
        <AsyncContentMui isLoading={loading}>
          {!covenants || covenants.length === 0 ? (
            <Stack alignItems="center" spacing={2} py={4}>
              <Typography color="text.secondary">Please define covenants you would like to have</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setDrawerOpen(true)}>
                Add Covenant
              </Button>
            </Stack>
          ) : (
            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
              {charts.map((covenant: Covenant, idx: number) => {
                const minSeries = covenant.graph.series.find((s: any) => s.name.toLowerCase() === "min");
                const maxSeries = covenant.graph.series.find((s: any) => s.name.toLowerCase() === "max");
                const mainSeries = covenant.graph.series.find(
                  (s: any) => s.name.toLowerCase() !== "min" && s.name.toLowerCase() !== "max"
                );
                const minValue = minSeries && minSeries.data.length > 0 ? minSeries.data[0] : "-";
                const maxValue = maxSeries && maxSeries.data.length > 0 ? maxSeries.data[0] : "-";
                const currentValue =
                  mainSeries && mainSeries.data.length > 0 ? mainSeries.data[mainSeries.data.length - 1] : null;

                return (
                  <Box
                    key={idx}
                    sx={{
                      position: "relative",
                      borderRadius: "8px",
                      boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
                      backgroundColor: "#fff",
                      borderLeft: "4px solid #e8dbba",
                      padding: "0 20px",
                      height: "186px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "flex-start"
                    }}
                  >
                    <Box position="absolute" top={8} right={8}>
                      <Menu
                        options={[
                          {
                            title: "Covenant settings",
                            icon: <ForwardToInboxIcon fontSize="sm" sx={{ color: "#9e9e9e" }} />,
                            onClick: () => {
                              setEditingCovenant(covenant);
                              setDrawerOpen(true);
                            }
                          },
                          {
                            title: "Remove",
                            icon: <RemoveIcon fontSize="sm" />,
                            onClick: () => {
                              if (dealId && covenant.id) {
                                deleteCovenant(dealId, covenant.id);
                              }
                            }
                          }
                        ]}
                      />
                    </Box>
                    <Box display="flex" height={"100%"}>
                      <Box pr={2} display="flex" flexDirection="column" justifyContent="center" minWidth={140}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={500}
                          color="text.primary"
                          sx={{ lineHeight: 1.5, mb: 2 }}
                        >
                          {covenant.name}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} mt={1} mb={0.5}>
                          Actual Index
                        </Typography>
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="text.primary"
                          sx={{ fontSize: 38, lineHeight: 1 }}
                        >
                          {typeof currentValue === "number" ? currentValue.toFixed(2) : "-"}
                        </Typography>
                        <Box mt={1}>
                          <Typography variant="subtitle2" color="success.main" fontWeight={600} display="inline">
                            {maxValue}
                          </Typography>
                          <Typography variant="body2" color="success.main" display="inline" ml={0.5}>
                            - Projected
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="subtitle2" color="error.main" fontWeight={600} display="inline">
                            {minValue}
                          </Typography>
                          <Typography variant="body2" color="error.main" display="inline" ml={0.5}>
                            - Minimum
                          </Typography>
                        </Box>
                      </Box>
                      <Box flex={1} flexGrow={1} display="flex" alignItems="center" justifyContent="flex-end">
                        <Box width={"100%"} height={85}>
                          <MemoizedHighchart options={covenant.graph} />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "divider",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  height: 186,
                  "&:hover": {
                    borderColor: "primary.main",
                    "& .add-icon": {
                      color: "primary.main"
                    }
                  }
                }}
                onClick={() => setDrawerOpen(true)}
              >
                <Typography variant="subtitle1" color="text.secondary">
                  Add Covenant
                </Typography>
                <AddCircleOutlineIcon sx={{ fontSize: 32, color: "text.secondary" }} />
              </Box>
            </Box>
          )}
        </AsyncContentMui>
        <AddCovenantDrawer
          open={drawerOpen}
          onClose={(changed) => {
            setDrawerOpen(false);
            setEditingCovenant(null);
            if (changed) {
              refetch();
            }
          }}
          covenant={editingCovenant}
        />
      </Box>
    </Stack>
  );
};
